import {
  ArrowRightIcon,
  Badge,
  Card,
  HStack,
  hoverSelector,
  focusWithinSelector,
  Text,
  VStack,
  View,
} from '@packages/systems/spring';
import {ComponentsCodeIcon} from '@packages/systems/spring-branded';
import {emotionStyled} from '@packages/utilities/styled';
import * as React from 'react';
import {useCodeComponentCreated} from './flux/CodeLibrariesSlice';
import {CodeComponentTemplatePicker} from './CodeComponentTemplatePicker';
import type {CodeComponentTemplateId} from '@packages/systems/code-libraries/core';

const ArrowWrapper = emotionStyled(View)({
  opacity: 0,
  transform: 'translateX(-4px)',
  transition: 'opacity 0.2s, transform 0.2s',
});

const BannerCard = emotionStyled(Card)({
  backgroundColor: '$background2',
  borderRadius: '$2',
  padding: '$6',
  width: 540,
  position: 'absolute',
  bottom: '$8',
  left: '50%',
  transform: 'translateX(-50%)',
  border: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  alignSelf: 'stretch',

  [`${hoverSelector}, ${focusWithinSelector}`]: {
    backgroundColor: '$background2',
    boxShadow: '$menu',
    [`& ${ArrowWrapper}`]: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },

  '&:focus-visible': {
    outline: '$blueBorder',
    outlineOffset: '1px',
  },
});

const CtaText = emotionStyled(HStack)({
  color: '$greenText',
});

export interface CodeComponentsBannerProps {
  onBannerViewed?: () => void;
  onCtaClicked?: () => void;
}

export const CodeComponentsBanner = ({
  onBannerViewed,
  onCtaClicked,
}: CodeComponentsBannerProps) => {
  const onCreateCodeComponent = useCodeComponentCreated();

  React.useEffect(() => {
    onBannerViewed?.();
  }, [onBannerViewed]);

  const handleCtaClick = (templateId?: CodeComponentTemplateId) => {
    onCtaClicked?.();
    onCreateCodeComponent({templateId});
  };

  return (
    <CodeComponentTemplatePicker onCreate={handleCtaClick}>
      <BannerCard as="button">
        <HStack spacing={4} align="start">
          <ComponentsCodeIcon colorVariant="green" size="48px" />
          <VStack spacing={4}>
            <VStack spacing={1}>
              <HStack spacing={2} align="center">
                <Text font="brandedDisplay6" colorVariant="primary">
                  AI code components
                </Text>
                <Badge colorVariant="green">New</Badge>
              </HStack>
              <Text font="body" colorVariant="secondary" textAlign="left">
                Generate interactive components — like forms, calculators, and
                quizzes — and use them across your site.
              </Text>
            </VStack>
            <CtaText spacing={1} align="center">
              <Text font="body">Generate code components</Text>
              <ArrowWrapper>
                <ArrowRightIcon colorVariant="inherit" />
              </ArrowWrapper>
            </CtaText>
          </VStack>
        </HStack>
      </BannerCard>
    </CodeComponentTemplatePicker>
  );
};
